import { useEffect, useState } from "react";
import type { AdsAssistantSocialPostRow } from "@/lib/adsAssistantClient";
import { answerKbQuestion } from "@/lib/kb/answer";

export function AdsAssistantKbSnippetDialog(props: {
  scopedOrgId: string | null;
  post: AdsAssistantSocialPostRow | null;
  onClose: () => void;
}) {
  const { scopedOrgId, post, onClose } = props;
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [answer, setAnswer] = useState<string | null>(null);
  const [sources, setSources] = useState<{ title: string }[]>([]);

  useEffect(() => {
    if (!post) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [post, onClose]);

  useEffect(() => {
    if (!post || !scopedOrgId) {
      setAnswer(null);
      setErr(null);
      setSources([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErr(null);
    setAnswer(null);
    setSources([]);
    const tags = post.tags ?? [];
    const tagPart = tags[0]?.label ? `Post tag: ${tags[0].label}. ` : "";
    const q = `${tagPart}Platform: ${post.platform}. What KB guidance applies to this saved ad? Ad copy:\n${(post.text_content || "").slice(0, 2000)}`;
    void answerKbQuestion({
      query: q,
      audience: "internal",
      organizationId: scopedOrgId,
      locationId: post.location_id?.trim() ? post.location_id : null,
      lane: "answers",
      topK: 6,
    })
      .then((r) => {
        if (cancelled) return;
        setAnswer(r.answer?.trim() ? r.answer : "(No answer text returned.)");
        setSources((r.sources ?? []).map((s) => ({ title: s.title })));
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setErr(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [post, scopedOrgId]);

  if (!post) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="ads-kb-snippet-title"
        className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-xl border border-gray-200 bg-white p-4 shadow-xl dark:border-gray-600 dark:bg-zinc-900"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2">
          <h2 id="ads-kb-snippet-title" className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            KB ideas
          </h2>
          <button
            type="button"
            className="rounded border border-gray-200 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-zinc-800"
            onClick={onClose}
          >
            Close
          </button>
        </div>
        <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">Internal KB search from this card (answers lane).</p>
        {loading ? <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">Loading…</p> : null}
        {err ? (
          <p className="mt-3 rounded border border-amber-200 bg-amber-50/90 px-2 py-2 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
            {err}
          </p>
        ) : null}
        {!loading && !err && answer ? (
          <div className="mt-3 space-y-2">
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-900 dark:text-gray-100">{answer}</p>
            {sources.length ? (
              <div>
                <p className="text-[11px] font-medium text-gray-600 dark:text-gray-400">Sources</p>
                <ul className="mt-1 list-disc pl-4 text-[11px] text-gray-700 dark:text-gray-300">
                  {sources.map((s, i) => (
                    <li key={`${s.title}-${i}`}>{s.title}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
